import React, { FC, ReactElement } from "react";
import { graphql, navigate } from "gatsby";
import { Helmet } from "react-helmet";
import Heading, { HeadingWeight } from "../components/Heading";
import Text, { TextSize } from "../components/Text";
import { Button, ButtonDecorator, ButtonSize, Card, LinkIconAnimation } from "kaleidoscope/src";
import Link from "../components/Link";
import { ArrowRight } from "kaleidoscope/src/global/icons";
import { getFirstPostsByCategory } from "../utils/posts";
import { ReactComponent as Docs } from "../assets/docs.svg";
import { ReactComponent as Reference } from "../assets/reference.svg";
import Footer from "../components/Footer";
import Code from "../components/Code";
import Img from "gatsby-image";

interface IHomeProps {
  data: {
    allSidebarYaml: {
      nodes: any;
    };
    site: {
      buildTime: string;
    };
    metadataYaml: any;
    file: any;
  };
}

const exampleRequest = {
  templateId: "6ee0f841f3cc8900090d82dc",
  name: "Sales proposal for Waratah INC.",
  published: true,
  substitutions: {
    title: "Sales proposal",
    client: "Waratah INC.",
    author: "Roy Campbell",
  },
};

const Home: FC<IHomeProps> = ({ data }) => {
  const categoryLinks = getFirstPostsByCategory(data.allSidebarYaml.nodes);

  return (
    <>
      <Helmet>
        <title>{data.metadataYaml.title}</title>
        <meta name="description" content={data.metadataYaml.description} />
      </Helmet>
      <div className="kld-home">
        <div className="kld-home__content">
          <div className="kld-home__header">
            <div className="kld-home__header-text">
              <Heading level={1} weight={HeadingWeight.Strong}>
                {data.metadataYaml.homepage.title}
              </Heading>
              <Text size={TextSize.XXLarge}>{data.metadataYaml.homepage.description}</Text>
              <Button
                className="kld-home__header-button"
                size={ButtonSize.Large}
                decorator={ButtonDecorator.Arrow}
                onClick={() => {
                  navigate(categoryLinks.docs.url);
                }}
              >
                Get started
              </Button>
            </div>
            <div className="kld-home__header-visual">
              <Code className="kld-home__header-code" language="json" code={JSON.stringify(exampleRequest, null, 2)} />
              <div className="kld-home__header-image-frame">
                <div className="kld-home__header-image">
                  <Img
                    fluid={data.file.childImageSharp.fluid}
                    alt="A browser displaying a Qwilr page generated via the API"
                  />
                </div>
              </div>
            </div>
          </div>
          <section className="kld-home__cards">
            <div className="kld-home__cards-content">
              <HomeCard
                icon={<Docs />}
                title="Docs"
                description="Quick start guides and example documentation to get started using the API as fast as possible"
                url={categoryLinks.docs.url}
                linkText="Browse docs"
              />
              <HomeCard
                icon={<Reference />}
                title="API Reference"
                description="Explore the full set of available REST API methods and authentication needed to use the API"
                url="/api-reference"
                linkText="Explore the API"
              />
            </div>
          </section>
        </div>
        <Footer className="kld-home__footer" modified={data.site.buildTime} />
      </div>
    </>
  );
};

interface IHomeCardProps {
  icon?: ReactElement;
  linkText: string;
  url: string;
  title: string;
  description: string;
}

const HomeCard: FC<IHomeCardProps> = ({ linkText, url, title, description, icon }) => (
  <Card className="kld-home__card">
    <Heading className="kld-home__card-title" level={3}>
      {icon}
      {title}
    </Heading>
    <Text size={TextSize.Large}>{description}</Text>
    <Text size={TextSize.Large}>
      <Link href={url} iconEnd={<ArrowRight />} iconAnimation={LinkIconAnimation.SlideEnd}>
        {linkText}
      </Link>
    </Text>
  </Card>
);

export const pageQuery = graphql`
  query HomeQuery {
    metadataYaml {
      description
      title
      homepage {
        title
        description
      }
    }
    allSidebarYaml {
      nodes {
        label
        categories {
          label
          pages
        }
      }
    }
    site {
      buildTime
    }
    file(relativePath: { eq: "api-browser-preview.png" }) {
      childImageSharp {
        fluid(maxWidth: 720) {
          ...GatsbyImageSharpFluid
        }
      }
    }
  }
`;

export default Home;
